import React from 'react';
import { HuchaValues } from '../models/hucha-values';
import { PortalContext } from '../../../../context/PortalContext';
import { FocusLayerSizeV2, ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import { HuchaPopupContent } from '../components/popup/popup-hucha-content';
import { useHuchaTracker } from './use-hucha-tracker';

// hook para manejar el popup/layer de la hucha
export const useHuchaPopup = (huchaValues: HuchaValues | null) => {
	const portalContext = React.useContext(PortalContext);
	const { portalService, layerManager } = portalContext;

	const [isPopupVisible, setIsPopupVisible] = React.useState(false);
	const layerRef = React.useRef<any>(null);
	const hasTrackedLayerLoadRef = React.useRef(false);
	const userVehicles = portalService.getPersonVehicles().getValue();
	const { trackOpenLayerClick, trackLayerLoad, trackLayerCloseClick } = useHuchaTracker(
		huchaValues?.huchaTier ?? 'standard',
	);

	const handlePopupOpen = () => {
		hasTrackedLayerLoadRef.current = false;
		trackOpenLayerClick();
		setIsPopupVisible(true);
	};

	React.useEffect(() => {
		if (userVehicles && isPopupVisible && !hasTrackedLayerLoadRef.current) {
			hasTrackedLayerLoadRef.current = true;
			trackLayerLoad();
		}
	}, [userVehicles, isPopupVisible, trackLayerLoad]);

	const handlePopupClose = () => {
		hasTrackedLayerLoadRef.current = false;
		setIsPopupVisible(false);
		trackLayerCloseClick();
	};

	React.useEffect(() => {
		if (!isPopupVisible) return;

		const layer = layerManager.openFocusLayer(
			() => (
				<HuchaPopupContent
					huchaValues={huchaValues}
					portalContext={portalContext}
					handlePopupClose={handlePopupClose}
				/>
			),
			{},
			{
				size: FocusLayerSizeV2.A,
				shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
			},
		);

		layerRef.current = layer;

		return () => {
			hasTrackedLayerLoadRef.current = false;
			layer.close();
			layerRef.current = null;
		};
	}, [isPopupVisible, huchaValues, portalContext, layerManager]);

	return {
		isPopupVisible,
		handlePopupOpen,
		handlePopupClose,
	};
};
